import { useState, useEffect, useMemo } from 'react';
import {
  PieChart, Pie, Cell,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ResponsiveContainer,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Input } from './ui/input';
import {
  Eye,
  Plus,
  Calendar,
  DollarSign,
  User,
  Home,
  TrendingUp,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Banknote,
  Loader2,
  Search,
} from 'lucide-react';
import { Deal, DealStage, DealMilestone } from '../types/deal';
import { DealDocumentChecklist } from './DealDocumentChecklist';
import { logger } from '@/services/logger.service';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { BridgemateDealModal } from './BridgemateDealModal';
import { NewDealModal } from './NewDealModal';
import { UpdateDealStatusModal } from './UpdateDealStatusModal';
import { toast } from 'sonner';
import { dealAPI } from '../lib/deal-api';

const stageConfig: Record<DealStage, { label: string; color: string }> = {
  'lead': { label: 'Lead', color: 'bg-gray-100 text-gray-800' },
  'viewing-scheduled': { label: 'Viewing Scheduled', color: 'bg-blue-100 text-blue-800' },
  'offer-made': { label: 'Offer Made', color: 'bg-yellow-100 text-yellow-800' },
  'offer-accepted': { label: 'Offer Accepted', color: 'bg-green-100 text-green-800' },
  'financing': { label: 'Financing', color: 'bg-purple-100 text-purple-800' },
  'inspection': { label: 'Inspection', color: 'bg-indigo-100 text-indigo-800' },
  'closing': { label: 'Closing', color: 'bg-orange-100 text-orange-800' },
  'closed': { label: 'Closed', color: 'bg-emerald-100 text-emerald-800' },
  'lost': { label: 'Lost', color: 'bg-red-100 text-red-800' },
};

// Maps backend status strings to frontend DealStage values
const STATUS_TO_STAGE: Record<string, DealStage> = {
  // Original backend values
  pending: 'lead',
  active: 'offer-made',
  viewing: 'viewing-scheduled',
  offer: 'offer-made',
  accepted: 'offer-accepted',
  financing: 'financing',
  inspection: 'inspection',
  closing: 'closing',
  closed: 'closed',
  completed: 'closed',
  cancelled: 'lost',
  lost: 'lost',
  withdrawn: 'lost',
  // Round-trip values (written by UpdateDealStatusModal via STAGE_TO_STATUS)
  'viewing-scheduled': 'viewing-scheduled',
  'offer-made': 'offer-made',
  'offer-accepted': 'offer-accepted',
  lead: 'lead',
};

const STAGE_PROBABILITY: Record<DealStage, number> = {
  lead: 10,
  'viewing-scheduled': 25,
  'offer-made': 40,
  'offer-accepted': 60,
  financing: 70,
  inspection: 80,
  closing: 90,
  closed: 100,
  lost: 0,
};

// Hex colours for recharts (cannot use Tailwind classes in SVG fills)
const STAGE_HEX: Record<DealStage, string> = {
  lead: '#9ca3af',
  'viewing-scheduled': '#3b82f6',
  'offer-made': '#eab308',
  'offer-accepted': '#22c55e',
  financing: '#a855f7',
  inspection: '#6366f1',
  closing: '#f97316',
  closed: '#10b981',
  lost: '#ef4444',
};

const ALL_STAGES: DealStage[] = [
  'lead', 'viewing-scheduled', 'offer-made', 'offer-accepted',
  'financing', 'inspection', 'closing', 'closed', 'lost',
];

// Normalizes a raw backend deal object to the frontend Deal interface
function normalizeDeal(raw: any): Deal {
  const rawId = raw.dealId || raw.id || raw.PK?.replace('DEAL#', '') || String(Date.now());
  const rawStatus = (raw.status || raw.stage || 'pending').toLowerCase();
  const stage: DealStage = STATUS_TO_STAGE[rawStatus] ?? 'lead';
  const createdAtMs = typeof raw.createdAt === 'number' ? raw.createdAt : Number(raw.createdAt) || Date.now();
  const salePrice = Number(raw.salePrice || raw.offerAmount || raw.salePrice || raw.price || 0);

  return {
    id: rawId,
    type: raw.dealType === 'rental' ? 'rental' : 'sale',
    propertyId: raw.propertyId || '',
    propertyTitle: raw.propertyTitle || raw.propertyAddress || raw.address || `Deal #${rawId.slice(0, 8)}`,
    propertyAddress: raw.propertyAddress || raw.address || '',
    stage,
    agentId: raw.agentId || '',
    agentName: raw.agentName || raw.agentId || '',
    clientId: raw.buyerId || raw.clientId || '',
    clientName: raw.buyerName || raw.clientName || raw.buyerEmail || '',
    sellerId: raw.sellerId,
    sellerName: raw.sellerName,
    offerAmount: salePrice,
    finalAmount: raw.finalAmount ? Number(raw.finalAmount) : (stage === 'closed' ? salePrice : undefined),
    commission: Number(raw.commission || raw.commissionValue || 0),
    commissionPercent: raw.commissionRate ? Number(raw.commissionRate) : undefined,
    createdDate: new Date(createdAtMs).toISOString(),
    offerDate: raw.offerDate,
    acceptedDate: raw.acceptedDate,
    expectedCloseDate: raw.expectedCloseDate,
    closedDate: raw.closedDate,
    conditions: raw.conditions,
    notes: raw.notes,
    documents: raw.documents,
    leaseTermMonths: raw.leaseTermMonths,
    monthlyRent: raw.monthlyRent ? Number(raw.monthlyRent) : undefined,
    depositAmount: raw.depositAmount ? Number(raw.depositAmount) : undefined,
    moveInDate: raw.moveInDate,
    probability: raw.probability != null ? Number(raw.probability) : STAGE_PROBABILITY[stage],
    lostReason: raw.lostReason,
    bridgeTypes: raw.bridgeTypes,
    listingId: raw.listingId || undefined,
    listingPhotoUrl: raw.listingPhotoUrl || undefined,
  };
}

export function DealPipeline() {
  const [viewMode, setViewMode] = useState<'kanban' | 'list'>('kanban');
  const [filterType, setFilterType] = useState<'all' | 'sale' | 'rental'>('all');
  const [clientSearch, setClientSearch] = useState('');
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);
  const [bridgemateModalOpen, setBridgemateModalOpen] = useState(false);
  const [newDealModalOpen, setNewDealModalOpen] = useState(false);
  const [updateDealStatusModalOpen, setUpdateDealStatusModalOpen] = useState(false);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDeals = async () => {
      try {
        const rawDeals = await dealAPI.getDeals();
        const fetchedDeals = (rawDeals as any[]).map(normalizeDeal);

        if (fetchedDeals.length === 0) {
          setDeals([]);
        } else {
          setDeals(fetchedDeals);
        }
      } catch (error) {
        logger.error('Error fetching deals', error as Error, undefined, 'DealPipeline');
        // Only show error toast for real errors, not empty data
        if (error instanceof Error && !error.message.includes('Server is down')) {
          toast.error('Failed to fetch deals. Please try again.');
        }
        setDeals([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDeals();
  }, []);

  const handleDealCreated = (newDeal: Deal) => {
    setDeals([...deals, newDeal]);
  };

  const handleDealUpdated = (dealId: string, updates: Partial<Deal>) => {
    setDeals(deals.map(deal => 
      deal.id === dealId 
        ? { ...deal, ...updates }
        : deal
    ));
    
    // Update selectedDeal if it's the one being updated
    if (selectedDeal?.id === dealId) {
      setSelectedDeal({ ...selectedDeal, ...updates });
    }
  };

  const filteredDeals = deals.filter(deal => {
    if (filterType !== 'all' && deal.type !== filterType) return false;
    if (clientSearch.trim()) {
      const q = clientSearch.toLowerCase();
      return (deal.clientName || '').toLowerCase().includes(q);
    }
    return true;
  });

  const dealsByStage = (stage: DealStage) => 
    filteredDeals.filter(d => d.stage === stage && d.stage !== 'closed' && d.stage !== 'lost');

  const activeDeals = filteredDeals.filter(d => d.stage !== 'closed' && d.stage !== 'lost');
  const closedDeals = filteredDeals.filter(d => d.stage === 'closed');
  const lostDeals = filteredDeals.filter(d => d.stage === 'lost');

  const totalPipelineValue = activeDeals.reduce((sum, deal) => sum + (deal.finalAmount || deal.offerAmount), 0);
  const totalClosedValue = closedDeals.reduce((sum, deal) => sum + (deal.finalAmount || 0), 0);

  // Chart data — recomputes automatically whenever filteredDeals changes (i.e. on every status update)
  const stageChartData = useMemo(() =>
    ALL_STAGES.map(stage => {
      const stageDeals = filteredDeals.filter(d => d.stage === stage);
      const totalValue = stageDeals.reduce((sum, d) => sum + (d.finalAmount || d.offerAmount || 0), 0);
      const weightedValue = stageDeals.reduce(
        (sum, d) => sum + (d.finalAmount || d.offerAmount || 0) * (d.probability / 100),
        0
      );
      return {
        stage,
        label: stageConfig[stage].label,
        shortLabel: stageConfig[stage].label.replace('viewing-scheduled', 'Viewing').split(' ')[0],
        count: stageDeals.length,
        value: totalValue,
        weightedValue: Math.round(weightedValue),
        avgDealSize: stageDeals.length > 0 ? Math.round(totalValue / stageDeals.length) : 0,
        color: STAGE_HEX[stage],
      };
    }),
  [filteredDeals]);

  const donutData = stageChartData.filter(d => d.count > 0);
  const barData = stageChartData.filter(d => d.value > 0);

  const weightedPipelineValue = useMemo(
    () => stageChartData.reduce((sum, d) => sum + d.weightedValue, 0),
    [stageChartData]
  );

  const conversionRate = activeDeals.length + closedDeals.length + lostDeals.length > 0
    ? (closedDeals.length / (activeDeals.length + closedDeals.length + lostDeals.length)) * 100
    : 0;

  const dealCommission = (d: Deal) =>
    d.commission && d.commission > 0
      ? d.commission
      : d.commissionPercent && d.commissionPercent > 0
        ? Math.round((d.finalAmount || d.offerAmount || 0) * (d.commissionPercent / 100))
        : 0;

  const closedThisMonth = closedDeals;
  const closedCommission = closedThisMonth.reduce((s, d) => s + dealCommission(d), 0);
  const pipelineCommission = activeDeals.reduce((s, d) => s + dealCommission(d), 0);

  const DonutTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.[0]) return null;
    const d = payload[0].payload;
    return (
      <div className="bg-background border rounded-lg p-3 shadow-lg text-sm space-y-1 min-w-[160px]">
        <p className="font-semibold" style={{ color: d.color }}>{d.label}</p>
        <p className="text-muted-foreground">{d.count} deal{d.count !== 1 ? 's' : ''}</p>
        <p className="font-medium">R {(d.value / 1_000_000).toFixed(2)}M total</p>
        <p className="text-green-600 text-xs">R {(d.weightedValue / 1_000_000).toFixed(2)}M weighted</p>
      </div>
    );
  };

  const BarTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    return (
      <div className="bg-background border rounded-lg p-3 shadow-lg text-sm space-y-1 min-w-[180px]">
        <p className="font-semibold">{d.label}</p>
        <p className="text-muted-foreground text-xs">{d.count} deal{d.count !== 1 ? 's' : ''}</p>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-sm" style={{ background: d.color }} />
          <span>Total: <strong>R {(d.value / 1_000_000).toFixed(2)}M</strong></span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-sm bg-green-500" />
          <span>Weighted: <strong>R {(d.weightedValue / 1_000_000).toFixed(2)}M</strong></span>
        </div>
        {d.avgDealSize > 0 && (
          <p className="text-xs text-muted-foreground pt-1 border-t">
            Avg deal: R {(d.avgDealSize / 1_000).toFixed(0)}k
          </p>
        )}
      </div>
    );
  };

  const DealCard = ({ deal }: { deal: Deal }) => {
    const milestones = ([] as DealMilestone[]).filter(m => m.dealId === deal.id);
    const completedMilestones = milestones.filter(m => m.completed).length;
    const totalMilestones = milestones.length;

    const amount = deal.finalAmount || deal.offerAmount || 0;

    return (
      <Card data-testid={`deal-row-${deal.id}`} className="cursor-pointer hover:shadow-md transition-shadow overflow-hidden" onClick={() => setSelectedDeal(deal)}>
        {/* Listing photo banner — shown when deal was created from a listing */}
        {deal.listingPhotoUrl && (
          <div className="relative h-24 w-full overflow-hidden">
            <img
              src={deal.listingPhotoUrl}
              alt={deal.propertyTitle}
              className="w-full h-full object-cover"
              onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          </div>
        )}
        <CardContent className="p-4">
          <div className="space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <h4 className="font-medium truncate text-sm">{deal.propertyTitle}</h4>
                {deal.propertyAddress && deal.propertyAddress !== deal.propertyTitle && (
                  <p className="text-xs text-muted-foreground truncate">{deal.propertyAddress}</p>
                )}
              </div>
              <Badge variant={deal.type === 'sale' ? 'default' : 'secondary'} className="text-xs flex-shrink-0">
                {deal.type}
              </Badge>
            </div>

            {deal.clientName && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <User className="h-3 w-3" />
                <span className="truncate">{deal.clientName}</span>
              </div>
            )}

            {deal.sellerName && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Home className="h-3 w-3" />
                <span className="truncate">Seller: {deal.sellerName}</span>
              </div>
            )}

            <div className="font-semibold text-sm">
              R {(amount ?? 0).toLocaleString()}
              {(deal.commission ?? 0) > 0 && (
                <span className="font-normal text-xs text-muted-foreground ml-1">
                  · Commission: R {(deal.commission ?? 0).toLocaleString()}
                </span>
              )}
            </div>

            {totalMilestones > 0 && (
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-muted rounded-full h-1.5">
                  <div
                    className="bg-primary h-full rounded-full"
                    style={{ width: `${(completedMilestones / totalMilestones) * 100}%` }}
                  />
                </div>
                <span className="text-xs text-muted-foreground">{completedMilestones}/{totalMilestones}</span>
              </div>
            )}

            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground truncate">
                {deal.agentName || (deal.agentId ? `Agent: ${deal.agentId.slice(0, 8)}` : '')}
              </span>
              <span className="font-medium text-green-600">{deal.probability}%</span>
            </div>

            <div className="text-xs text-muted-foreground">
              {new Date(deal.createdDate).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2>Deal Pipeline</h2>
          <p className="text-muted-foreground">
            Manage and track all sales and rental deals
          </p>
        </div>
        <Button
          onClick={() => setNewDealModalOpen(true)}
          data-testid="deal-create-trigger"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Deal
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card data-testid="analytics-deal-active-deals-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium" data-testid="analytics-deal-active-deals-label">Active Deals</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="analytics-deal-active-deals-count">{activeDeals.length}</div>
            <p className="text-xs text-muted-foreground">In pipeline</p>
          </CardContent>
        </Card>

        <Card data-testid="analytics-deal-pipeline-value-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium" data-testid="analytics-deal-pipeline-value-label">Pipeline Value</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="analytics-deal-pipeline-value">R{(totalPipelineValue / 1_000_000).toFixed(1)}M</div>
            <p className="text-xs text-muted-foreground">Total potential</p>
          </CardContent>
        </Card>

        <Card data-testid="analytics-deal-weighted-pipeline-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-green-700 dark:text-green-400">Weighted Pipeline</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700 dark:text-green-400">
              R{(weightedPipelineValue / 1_000_000).toFixed(1)}M
            </div>
            <p className="text-xs text-muted-foreground">Probability-adjusted</p>
          </CardContent>
        </Card>

        <Card data-testid="analytics-deal-closed-month-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium" data-testid="analytics-deal-closed-month-label">Closed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="analytics-deal-closed-month-count">{closedDeals.length}</div>
            <p className="text-xs text-muted-foreground" data-testid="analytics-deal-closed-month-value">R{(totalClosedValue / 1_000_000).toFixed(1)}M value</p>
          </CardContent>
        </Card>

        <Card data-testid="analytics-deal-conversion-rate-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium" data-testid="analytics-deal-conversion-rate-label">Win Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="analytics-deal-conversion-rate-value">
              {conversionRate.toFixed(0)}%
            </div>
            <p className="text-xs text-muted-foreground">Closed / total</p>
          </CardContent>
        </Card>
      </div>

      {/* Commission Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card data-testid="analytics-deal-commission-closed-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5">
              <Banknote className="w-4 h-4" />
              Commission Earned
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400" data-testid="analytics-deal-commission-closed-value">
              R {closedCommission > 0 ? closedCommission.toLocaleString() : '—'}
            </div>
            <p className="text-xs text-muted-foreground">
              From {closedThisMonth.length} closed deal{closedThisMonth.length !== 1 ? 's' : ''}
            </p>
          </CardContent>
        </Card>

        <Card data-testid="analytics-deal-commission-pipeline-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-amber-600 dark:text-amber-400 flex items-center gap-1.5">
              <TrendingUp className="w-4 h-4" />
              Pipeline Commission
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600 dark:text-amber-400" data-testid="analytics-deal-commission-pipeline-value">
              R {pipelineCommission > 0 ? pipelineCommission.toLocaleString() : '—'}
            </div>
            <p className="text-xs text-muted-foreground">
              Potential across {activeDeals.length} active deal{activeDeals.length !== 1 ? 's' : ''}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Pipeline Analytics Charts */}
      {deals.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Donut — Stage Mix */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center justify-between">
                <span>Deal Stage Mix</span>
                <span className="text-xs font-normal text-muted-foreground">
                  {filteredDeals.length} total · hover for details
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                {/* Donut */}
                <div className="relative flex-shrink-0" style={{ width: 180, height: 180 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={donutData}
                        cx="50%"
                        cy="50%"
                        innerRadius={52}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="count"
                      >
                        {donutData.map((entry) => (
                          <Cell key={entry.stage} fill={entry.color} stroke="none" />
                        ))}
                      </Pie>
                      <RechartsTooltip content={<DonutTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                  {/* Centre label */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <span className="text-2xl font-bold">{filteredDeals.length}</span>
                    <span className="text-xs text-muted-foreground">deals</span>
                  </div>
                </div>

                {/* Legend */}
                <div className="flex-1 space-y-1.5 min-w-0">
                  {donutData.map(d => (
                    <div key={d.stage} className="flex items-center justify-between gap-2 text-xs">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: d.color }} />
                        <span className="truncate text-muted-foreground">{d.label}</span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="font-semibold">{d.count}</span>
                        <span className="text-muted-foreground w-12 text-right">
                          {filteredDeals.length > 0 ? ((d.count / filteredDeals.length) * 100).toFixed(0) : 0}%
                        </span>
                      </div>
                    </div>
                  ))}
                  {donutData.length === 0 && (
                    <p className="text-xs text-muted-foreground text-center py-4">No deals to display</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Bar Chart — Value by Stage */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center justify-between">
                <span>Value by Stage</span>
                <div className="flex items-center gap-3 text-xs font-normal">
                  <span className="flex items-center gap-1">
                    <span className="w-2.5 h-2.5 rounded-sm inline-block bg-[#6366f1]" />
                    Total
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2.5 h-2.5 rounded-sm inline-block bg-[#10b981]" />
                    Weighted
                  </span>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {barData.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={barData} margin={{ top: 4, right: 4, bottom: 4, left: 0 }} barGap={2}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                    <XAxis
                      dataKey="shortLabel"
                      tick={{ fontSize: 10, fill: '#ffffff' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tickFormatter={(v) => v >= 1_000_000 ? `R${(v / 1_000_000).toFixed(1)}M` : v >= 1_000 ? `R${(v / 1_000).toFixed(0)}k` : `R${v}`}
                      tick={{ fontSize: 10, fill: '#ffffff' }}
                      axisLine={false}
                      tickLine={false}
                      width={52}
                    />
                    <RechartsTooltip content={<BarTooltip />} cursor={{ fill: 'hsl(var(--muted))', opacity: 0.5 }} />
                    <Bar dataKey="value" name="Total Value" radius={[3, 3, 0, 0]} maxBarSize={28}>
                      {barData.map(entry => (
                        <Cell key={entry.stage} fill={entry.color} />
                      ))}
                    </Bar>
                    <Bar dataKey="weightedValue" name="Weighted Value" fill="#10b981" radius={[3, 3, 0, 0]} maxBarSize={28} opacity={0.75} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[200px] text-sm text-muted-foreground">
                  No deal values to display
                </div>
              )}

              {/* Value bucket summary row */}
              {barData.length > 0 && (
                <div className="mt-3 pt-3 border-t grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <p className="text-muted-foreground">Top Stage</p>
                    <p className="font-semibold truncate">
                      {[...barData].sort((a, b) => b.value - a.value)[0]?.label ?? '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Largest Deal</p>
                    <p className="font-semibold">
                      R {(Math.max(...filteredDeals.map(d => d.finalAmount || d.offerAmount || 0)) / 1_000).toFixed(0)}k
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Avg Deal Size</p>
                    <p className="font-semibold">
                      R {filteredDeals.length > 0
                        ? ((filteredDeals.reduce((s, d) => s + (d.finalAmount || d.offerAmount || 0), 0) / filteredDeals.length) / 1_000).toFixed(0)
                        : 0}k
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters, Search and View Toggle */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex gap-2 flex-wrap">
          <Button
            variant={filterType === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterType('all')}
            data-testid="deal-filter-all"
          >
            All Deals
          </Button>
          <Button
            variant={filterType === 'sale' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterType('sale')}
            data-testid="deal-filter-sale"
          >
            Sales Only
          </Button>
          <Button
            variant={filterType === 'rental' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterType('rental')}
            data-testid="deal-filter-rental"
          >
            Rentals Only
          </Button>
        </div>

        <div className="flex items-center gap-3 sm:ml-auto">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
            <Input
              value={clientSearch}
              onChange={e => setClientSearch(e.target.value)}
              placeholder="Search by client name..."
              className="pl-8 h-8 w-52 text-sm"
              data-testid="deal-client-search"
            />
          </div>
          <Tabs value={viewMode} onValueChange={(v: any) => setViewMode(v)}>
            <TabsList>
              <TabsTrigger value="kanban">Kanban</TabsTrigger>
              <TabsTrigger value="list">List</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Kanban View */}
      {viewMode === 'kanban' && (
        <div className="overflow-x-auto">
          <div className="inline-flex gap-4 pb-4 min-w-full">
            {(['lead', 'viewing-scheduled', 'offer-made', 'offer-accepted', 'financing', 'inspection', 'closing'] as DealStage[]).map(stage => {
              const deals = dealsByStage(stage);
              const stageValue = deals.reduce((sum, d) => sum + (d.finalAmount || d.offerAmount), 0);

              return (
                <div key={stage} className="flex-shrink-0 w-80">
                  <div className="bg-muted/50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="font-medium text-sm">{stageConfig[stage].label}</h3>
                        <p className="text-xs text-muted-foreground">
                          {deals.length} deals · R{(stageValue / 1000).toFixed(0)}k
                        </p>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {deals.length}
                      </Badge>
                    </div>

                    <div className="space-y-3 max-h-[600px] overflow-y-auto">
                      {deals.map(deal => (
                        <DealCard key={deal.id} deal={deal} />
                      ))}
                      {deals.length === 0 && (
                        <div className="text-center text-muted-foreground text-sm py-8">
                          No deals in this stage
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <Card>
          <CardContent className="p-0">
            <div className="divide-y">
              {filteredDeals.map(deal => {
                const milestones = ([] as DealMilestone[]).filter(m => m.dealId === deal.id);
                const completedMilestones = milestones.filter(m => m.completed).length;

                return (
                  <div 
                    key={deal.id} 
                    className="p-4 hover:bg-muted/50 cursor-pointer"
                    onClick={() => setSelectedDeal(deal)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="font-medium">{deal.propertyTitle}</h4>
                          <Badge variant="outline" className={(stageConfig[deal.stage] || stageConfig['lead']).color}>
                            {(stageConfig[deal.stage] || stageConfig['lead']).label}
                          </Badge>
                          <Badge variant={deal.type === 'sale' ? 'default' : 'secondary'}>
                            {deal.type}
                          </Badge>
                        </div>

                        <div className="flex items-center gap-6 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {deal.clientName}
                          </span>
                          <span className="flex items-center gap-1">
                            <Home className="h-3 w-3" />
                            {deal.propertyAddress}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {new Date(deal.createdDate).toLocaleDateString()}
                          </span>
                        </div>
                      </div>

                      <div className="text-right">
                        <div className="font-semibold mb-1">
                          R {(deal.finalAmount || deal.offerAmount || 0).toLocaleString()}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {milestones.length > 0 && (
                            <span>{completedMilestones}/{milestones.length} milestones</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Deal Details Dialog */}
      <Dialog open={!!selectedDeal} onOpenChange={() => setSelectedDeal(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          {selectedDeal && (
            <>
              <DialogHeader>
                <DialogTitle>{selectedDeal.propertyTitle}</DialogTitle>
                <DialogDescription>
                  {selectedDeal.propertyAddress} · {selectedDeal.type === 'sale' ? 'Sale' : 'Rental'} Deal
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6">
                {/* Status and Value */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Status</Label>
                    <Badge className={`mt-1 ${(stageConfig[selectedDeal.stage] || stageConfig['lead']).color}`}>
                      {(stageConfig[selectedDeal.stage] || stageConfig['lead']).label}
                    </Badge>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Deal Value</Label>
                    <p className="font-semibold mt-1">
                      R {(selectedDeal.finalAmount || selectedDeal.offerAmount || 0).toLocaleString()}
                    </p>
                  </div>
                </div>

                {/* Parties */}
                <div>
                  <h4 className="font-medium mb-3">Parties Involved</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-muted-foreground">Client / Buyer</Label>
                      <p className="mt-1">{selectedDeal.clientName || selectedDeal.clientId || '—'}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Agent</Label>
                      <p className="mt-1">{selectedDeal.agentName || selectedDeal.agentId || '—'}</p>
                    </div>
                    {selectedDeal.sellerName && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Seller</Label>
                        <p className="mt-1">{selectedDeal.sellerName}</p>
                      </div>
                    )}
                    <div>
                      <Label className="text-xs text-muted-foreground">Deal Reference</Label>
                      <p className="mt-1 font-mono text-xs">{selectedDeal.id}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Created</Label>
                      <p className="mt-1">{new Date(selectedDeal.createdDate).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                    </div>
                    {(selectedDeal.commission ?? 0) > 0 && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Commission</Label>
                        <p className="mt-1 text-green-700 font-medium">R {(selectedDeal.commission ?? 0).toLocaleString()}</p>
                      </div>
                    )}
                    {selectedDeal.expectedCloseDate && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Expected Close</Label>
                        <p className="mt-1">{new Date(selectedDeal.expectedCloseDate).toLocaleDateString('en-ZA')}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Milestones */}
                <div>
                  <h4 className="font-medium mb-3">Milestones</h4>
                  <div className="space-y-2">
                    {([] as DealMilestone[])
                      .filter(m => m.dealId === selectedDeal.id)
                      .map(milestone => (
                        <div key={milestone.id} className="flex items-center gap-3 p-3 border rounded-lg">
                          {milestone.completed ? (
                            <CheckCircle className="h-5 w-5 text-green-600" />
                          ) : (
                            <Clock className="h-5 w-5 text-muted-foreground" />
                          )}
                          <div className="flex-1">
                            <p className="font-medium text-sm">{milestone.title}</p>
                            <p className="text-xs text-muted-foreground">
                              Due: {new Date(milestone.dueDate).toLocaleDateString()}
                              {milestone.completedDate && ` · Completed: ${new Date(milestone.completedDate).toLocaleDateString()}`}
                            </p>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Notes */}
                {selectedDeal.notes && (
                  <div>
                    <h4 className="font-medium mb-2">Notes</h4>
                    <p className="text-sm text-muted-foreground">{selectedDeal.notes}</p>
                  </div>
                )}

                {/* Deal Documents */}
                <div>
                  <h4 className="font-medium mb-3">Deal Documents</h4>
                  <DealDocumentChecklist dealId={selectedDeal.id} />
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2 pt-4 border-t">
                  {selectedDeal.stage === 'closed' && (
                    <Button
                      className="flex-1 bg-blue-600 hover:bg-blue-700"
                      onClick={(e) => {
                        e.stopPropagation();
                        setBridgemateModalOpen(true);
                      }}
                      data-testid="deal-send-bridgemate"
                    >
                      <Banknote className="h-4 w-4 mr-2" />
                      Send to Bridgemate
                    </Button>
                  )}
                  <Button
                    className="flex-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      setUpdateDealStatusModalOpen(true);
                    }}
                    data-testid="deal-update-status"
                  >
                    Update Status
                  </Button>
                  <Button variant="outline" className="flex-1" data-testid="deal-add-note">Add Note</Button>
                  <Button variant="outline" data-testid="deal-view">
                    <Eye className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Bridgemate Deal Modal */}
      <BridgemateDealModal
        open={bridgemateModalOpen} 
        onOpenChange={setBridgemateModalOpen} 
        deal={selectedDeal}
      />

      {/* New Deal Modal */}
      <NewDealModal
        open={newDealModalOpen}
        onOpenChange={setNewDealModalOpen}
        onDealCreated={handleDealCreated}
      />

      {/* Update Deal Status Modal */}
      <UpdateDealStatusModal
        open={updateDealStatusModalOpen}
        onOpenChange={setUpdateDealStatusModalOpen}
        deal={selectedDeal}
        onDealUpdated={handleDealUpdated}
      />
    </div>
  );
}

function Label({ children, className }: { children: React.ReactNode; className?: string }) {
  return <label className={className}>{children}</label>;
}